import { useState } from "react";
import { tipsRequest } from "../helpers/tipsWorkflow";
export default function ControlledCalibrationBatch({
  profile,
  market,
  language,
  disabled,
  onBusy,
}) {
  const [inventory, setInventory] = useState(null),
    [run, setRun] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const en = language?.startsWith("en"),
    t = (de, eng) => (en ? eng : de);
  const request = async (body) => {
    const r = await tipsRequest("league-analysis", body);
    if (!r || r.success === false) throw Error(r?.code || "REQUEST_FAILED");
    return r;
  };
  const act = async (fn) => {
    setBusy(true);
    onBusy?.(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      onBusy?.(false);
    }
  };
  const advance = async (initial) => {
    let r = initial;
    setRun(r);
    while (r.status === "running") {
      r = (
        await request({
          mode: "calibration-run-step",
          run_id: r.id,
          index: r.completed,
        })
      ).run;
      setRun(r);
    }
  };
  const start = (save) =>
    act(async () => {
      if (
        !window.confirm(
          t(
            save
              ? "Alle geeigneten aktuellen Ligen berechnen und speichern? Snapshots werden angelegt. Keine historische Neuberechnung."
              : "Nur analysieren, ohne Settings zu speichern?",
            save
              ? "Calculate and save all eligible current leagues? Snapshots will be created. No historical recalculation."
              : "Analyze only, without saving settings?"
          )
        )
      )
        return;
      await advance(
        (
          await request({
            mode: "calibration-run-start",
            run_id: `batch-${crypto.randomUUID()}`,
            confirm: true,
            profile,
            market_orientation: market,
            language: en ? "en" : "de",
            save,
          })
        ).run
      );
    });
  const locked = busy || disabled;
  return (
    <section aria-label={t("Kontrollierter Gesamtlauf", "Controlled full run")}>
      <p>
        {t(
          "Aktuelle reguläre Ligen · serverseitige Doppelstart-Sperre",
          "Current regular leagues · server-side run lock"
        )}
      </p>
      <button
        disabled={locked}
        onClick={() =>
          act(async () =>
            setInventory(
              (await request({ mode: "calibration-run-inventory" })).inventory
            )
          )
        }
      >
        {t("Scope-Vorschau laden", "Load scope preview")}
      </button>{" "}
      <button
        disabled={locked}
        onClick={() =>
          act(async () =>
            setRun((await request({ mode: "calibration-run-status" })).run)
          )
        }
      >
        {t("Laufstatus laden", "Load run status")}
      </button>
      {inventory && (
        <>
          <p>
            {inventory.competitions} {t("Wettbewerbe", "competitions")} ·{" "}
            {inventory.regular_leagues} {t("Ligen", "leagues")} ·{" "}
            {inventory.selected}{" "}
            {t(
              "aktuelle Saisons; Datenprüfung vor jedem Save",
              "current seasons; data validation before every save"
            )}
          </p>
          <button
            disabled={locked || !inventory.selected}
            onClick={() => start(false)}
          >
            {t("Nur analysieren / Dry Run", "Analyze only / Dry run")}
          </button>{" "}
          <button
            disabled={locked || !inventory.selected}
            onClick={() => start(true)}
          >
            {t(
              "Alle geeigneten Ligen kalibrieren und speichern",
              "Calibrate and save all eligible leagues"
            )}
          </button>
          <details>
            <summary>
              {t("Scope und Ausschlüsse", "Scope and exclusions")}
            </summary>
            {inventory.items.map((i) => (
              <p key={i.league_id}>
                {i.league_name}: {i.season_name || i.reason}
                {i.excluded.map((s) => ` · ${s.name}: ${s.reason}`).join("")}
              </p>
            ))}
          </details>
        </>
      )}
      {run && (
        <>
          <p role="status">
            {run.id} · {run.status} · {run.completed}/{run.total} ·{" "}
            {run.status === "running"
              ? t(
                  "Analysieren → normalisieren → ggf. speichern → prüfen",
                  "Analyze → normalize → optionally save → verify"
                )
              : ""}
          </p>
          {run.status === "running" && (
            <button disabled={locked} onClick={() => act(() => advance(run))}>
              {t("Unterbrochenen Lauf fortsetzen", "Resume interrupted run")}
            </button>
          )}
          <div className="le-scroll">
            <table>
              <thead>
                <tr>
                  {[
                    t("Liga", "League"),
                    t("Saison", "Season"),
                    t("Teams", "Teams"),
                    t("Änderungen", "Changes"),
                    "Status",
                  ].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.values(run.items).map((i) => (
                  <tr key={i.season_id}>
                    <td>{i.league_name}</td>
                    <td>{i.season_id}</td>
                    <td>{i.summary?.teams ?? "—"}</td>
                    <td>{i.summary?.changed_fields ?? "—"}</td>
                    <td>
                      {i.status}
                      {i.error ? `: ${i.error}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {run?.status === "running" && <button disabled={locked} onClick={()=>act(async()=>{
        if(window.confirm(t("Lauf nach dem laufenden Schritt beenden? Bereits gespeicherte Ligen bleiben gespeichert.","Stop after the current step? Previously saved leagues remain saved.")))
          setRun((await request({mode:"calibration-run-abort",run_id:run.id})).run);
      })}>{t("Lauf abbrechen", "Cancel run")}</button>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
